import { z } from 'zod';

/**
 * SIL-821 — the six event types asset_provenance_event
 * (db/schema/805_asset_provenance_log.sql) accepts. Order here is
 * documentation only; the chain itself is ordered by `seq`, assigned by
 * the database.
 */
export const provenanceEventTypeSchema = z.enum(['creation', 'transfer', 'disclosure', 'merge', 'split', 'dispute']);
export type ProvenanceEventType = z.infer<typeof provenanceEventTypeSchema>;

export const provenanceDisputePhaseSchema = z.enum(['opened', 'resolved']);
export type ProvenanceDisputePhase = z.infer<typeof provenanceDisputePhaseSchema>;

/**
 * One rendered chain entry for the Passport (assemble.ts).
 *
 * `customerOrgHolder` / `counterpartyOrgHolder` are pre-rendered DISPLAY
 * STRINGS (SIL-823, via fn_render_asset_provenance_holder — db/schema/
 * 808_asset_provenance_alias.sql), never a raw org name or id: either the
 * org's per-(asset,org) alias ("Verified Holder A7F3", unlinkable across
 * assets) or, only if that org opted in, its legal name. Unlike the prior
 * owner-only redaction of a raw counterparty name, this field is safe to
 * show to ANY viewer (owner or not) — the alias itself is the redaction
 * mechanism now, not a null-for-non-owner gate. NULL means no org is named
 * on that role for this event (e.g. a disclosure event's customerOrgHolder,
 * or a creation event with no counterparty) — not "unknown holder", which
 * the render function guarantees never happens for an actual org.
 *
 * `manufacturerSerial` is still redacted (null) for a non-owner viewer by
 * the assembler, the same masking discipline as
 * `passportIdentitySchema.serialNumber` — a dispute event never carries
 * either field regardless of viewer (805's CHECK constraint means there is
 * nothing to redact).
 */
export const provenanceEventClientSchema = z.object({
  id: z.string().uuid(),
  seq: z.number().int().positive(),
  eventType: provenanceEventTypeSchema,
  occurredAt: z.string(),
  customerOrgHolder: z.string().nullable(),
  counterpartyOrgHolder: z.string().nullable(),
  manufacturerSerial: z.string().nullable(),
  relatedAssetId: z.string().uuid().nullable(),
  disputePhase: provenanceDisputePhaseSchema.nullable(),
});
export type ProvenanceEventClient = z.infer<typeof provenanceEventClientSchema>;
